/**
 * 認証フック + AuthProvider
 *
 * AWS Amplify (Cognito) ベースの認証
 * DEV/PROD 共通で実 Cognito を使用する
 *
 * SPEC 15.4 準拠
 */
// steering 551 / T6: logout / identity 変更で fetch cache を破棄する。
import { clearAll, setIdentityScope } from '@/api/fetchCache';
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';
import type { AuthUser, UserRole } from '@/types';
import type { SignInOutput } from 'aws-amplify/auth';

export interface MfaChallenge {
  kind: 'SMS' | 'TOTP' | 'SELECT';
  destination?: string;
  allowedTypes?: ('SMS' | 'TOTP')[];
}
import { useIdleTimeout } from '@/hooks/useIdleTimeout';

// ==============================
// Context 定義
// ==============================

interface AuthContextValue {
  /** 認証済みユーザー (未認証なら null) */
  user: AuthUser | null;
  /** 認証状態の読み込み中フラグ */
  loading: boolean;
  /** ログイン */
  login(email: string, password: string): Promise<void>;
  /** ログアウト */
  logout(): Promise<void>;
  /** 初回ログイン時のパスワード変更が必要か */
  needsNewPassword: boolean;
  /** 初回パスワード変更を完了する */
  completeNewPassword(newPassword: string): Promise<void>;
  mfaChallenge: MfaChallenge | null;
  completeMfa(response: string): Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// ==============================
// Amplify (Cognito) 認証
// ==============================

function useCognitoAuth(): AuthContextValue {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [needsNewPassword, setNeedsNewPassword] = useState(false);
  const [mfaChallenge, setMfaChallenge] = useState<MfaChallenge | null>(null);
  // signIn の中間状態を保持 (NEW_PASSWORD_REQUIRED 対応)
  const [pendingSignInOutput, setPendingSignInOutput] = useState<unknown>(null);

  /** Amplify を動的 import して設定する */
  const initAmplify = useCallback(async () => {
    const { Amplify } = await import('aws-amplify');
    Amplify.configure({
      Auth: {
        Cognito: {
          userPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID as string,
          userPoolClientId: import.meta.env.VITE_COGNITO_CLIENT_ID as string,
        },
      },
    });
  }, []);

  /** JWT からユーザー情報を抽出する */
  const extractUserFromSession = useCallback(async (): Promise<AuthUser | null> => {
    const { fetchAuthSession, getCurrentUser } = await import('aws-amplify/auth');
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken;
      if (!idToken) return null;

      const currentUser = await getCurrentUser();
      const payload = idToken.payload as Record<string, unknown>;

      // auth_time は JWT に含まれる認証時刻 (UNIX timestamp)
      const authTime = payload['auth_time'] as number | undefined;
      const lastLoginAt = authTime ? new Date(authTime * 1000).toISOString() : null;

      return {
        userId: currentUser.userId,
        email: (payload['email'] as string) ?? '',
        orgId: (payload['custom:org_id'] as string) ?? '',
        orgName: (payload['custom:org_name'] as string) ?? '',
        role: (payload['custom:role'] as UserRole) ?? 'member',
        name: (payload['name'] as string) || '',
        lastLoginAt,
      };
    } catch {
      return null;
    }
  }, []);

  /** 初期化: Amplify 設定 + 既存セッション復元 */
  useEffect(() => {
    let cancelled = false;
    const init = async () => {
      await initAmplify();
      const restored = await extractUserFromSession();
      if (!cancelled) {
        setUser(restored);
        setLoading(false);
      }
    };
    init();
    return () => {
      cancelled = true;
    };
  }, [initAmplify, extractUserFromSession]);

  /** タブ復帰時に JWT を自動リフレッシュする */
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState !== 'visible') return;
      const refreshed = await extractUserFromSession();
      if (refreshed) {
        setUser(refreshed);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [extractUserFromSession]);

  /** 初回パスワード変更の後も含めて Cognito の次の段階を同じ判定で処理する。 */
  const applySignInOutput = useCallback(
    async (output: SignInOutput) => {
      setNeedsNewPassword(false);
      setMfaChallenge(null);
      if (output.isSignedIn) {
        const authUser = await extractUserFromSession();
        if (!authUser)
          throw new Error('認証セッションを確認できませんでした。ログインし直してください');
        setUser(authUser);
        setPendingSignInOutput(null);
        try {
          const { apiFetch } = await import('@/api/client');
          apiFetch('/admin/users/me/last-login', { method: 'PUT' }).catch(() => {});
        } catch {
          // 最終ログイン記録の失敗は認証を妨げない。
        }
        return;
      }
      setUser(null);
      const step = output.nextStep;
      switch (step.signInStep) {
        case 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED':
          setPendingSignInOutput(output);
          setNeedsNewPassword(true);
          return;
        case 'CONFIRM_SIGN_IN_WITH_SMS_CODE':
          setMfaChallenge({ kind: 'SMS', destination: step.codeDeliveryDetails?.destination });
          return;
        case 'CONFIRM_SIGN_IN_WITH_TOTP_CODE':
          setMfaChallenge({ kind: 'TOTP' });
          return;
        case 'CONTINUE_SIGN_IN_WITH_MFA_SELECTION': {
          const allowedTypes =
            step.allowedMFATypes?.filter(
              (type): type is 'SMS' | 'TOTP' => type === 'SMS' || type === 'TOTP',
            ) ?? [];
          if (allowedTypes.length > 0) {
            setMfaChallenge({ kind: 'SELECT', allowedTypes });
            return;
          }
          break;
        }
      }
      // 未対応の設定・属性不足を認証成功と取り違えない。
      setPendingSignInOutput(null);
      throw new Error(
        '追加認証の設定を完了できません。管理者に電話番号と認証方式の確認を依頼してください',
      );
    },
    [extractUserFromSession],
  );

  const login = useCallback(
    async (email: string, password: string) => {
      const { signIn } = await import('aws-amplify/auth');
      setNeedsNewPassword(false);
      setMfaChallenge(null);
      setPendingSignInOutput(null);
      let output: SignInOutput;
      try {
        output = await signIn({ username: email, password });
      } catch (err) {
        const { localizeCognitoError } = await import('@/utils/cognitoErrorLabels');
        throw new Error(localizeCognitoError(err, 'ログインに失敗しました'), { cause: err });
      }
      await applySignInOutput(output);
    },
    [applySignInOutput],
  );

  const logout = useCallback(async () => {
    const { signOut } = await import('aws-amplify/auth');
    await signOut();
    // steering 551 / T6: 前ユーザーのデータを次のユーザーへ残さない。
    // `Header.tsx` の SPA logout は `navigate()` でリロードを伴わない唯一の経路なので、
    // ここで明示的に捨てないと in-memory cache が生き残る。
    clearAll();
    setUser(null);
    setNeedsNewPassword(false);
    setPendingSignInOutput(null);
    setMfaChallenge(null);
  }, []);

  const completeNewPassword = useCallback(
    async (newPassword: string) => {
      if (!pendingSignInOutput || !needsNewPassword)
        throw new Error('パスワード変更が必要な状態ではありません');
      const { confirmSignIn } = await import('aws-amplify/auth');
      let output: SignInOutput;
      try {
        output = await confirmSignIn({ challengeResponse: newPassword });
      } catch (err) {
        const { localizeCognitoError } = await import('@/utils/cognitoErrorLabels');
        throw new Error(localizeCognitoError(err, 'パスワード変更に失敗しました'), { cause: err });
      }
      await applySignInOutput(output);
    },
    [pendingSignInOutput, needsNewPassword, applySignInOutput],
  );

  const completeMfa = useCallback(
    async (response: string) => {
      if (!mfaChallenge) throw new Error('追加認証が必要な状態ではありません');
      if (mfaChallenge.kind === 'SELECT') {
        if (!mfaChallenge.allowedTypes?.some((type) => type === response))
          throw new Error('利用できる認証方式を選択してください');
      } else if (!/^\d{6}$/.test(response.trim())) {
        throw new Error('6 桁の確認コードを入力してください');
      }
      const { confirmSignIn } = await import('aws-amplify/auth');
      let output: SignInOutput;
      try {
        output = await confirmSignIn({ challengeResponse: response.trim() });
      } catch (err) {
        const { localizeCognitoError } = await import('@/utils/cognitoErrorLabels');
        throw new Error(localizeCognitoError(err, '追加認証に失敗しました'), { cause: err });
      }
      await applySignInOutput(output);
    },
    [mfaChallenge, applySignInOutput],
  );

  /**
   * 返り値を memo 化する (#406 Gate 8D-FR)。
   *
   * ここが毎 render 新しいオブジェクトになると、`AuthProvider` がそのまま Context の value に渡すため
   * **全 consumer が毎 render 再描画**され、value を dependency に持つ callback / effect も
   * 毎 render 再生成・再登録される (timer の張り直し等)。認証済み shell が再描画ループに入る原因になるため、
   * 中身が変わったときだけ identity が変わるようにする。
   */
  return useMemo(
    () => ({
      user,
      loading,
      login,
      logout,
      needsNewPassword,
      completeNewPassword,
      mfaChallenge,
      completeMfa,
    }),
    [
      user,
      loading,
      login,
      logout,
      needsNewPassword,
      completeNewPassword,
      mfaChallenge,
      completeMfa,
    ],
  );
}

// ==============================
// AuthProvider
// ==============================

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * 認証コンテキストプロバイダー
 *
 * DEV/PROD 共通で実 Cognito 認証を使用する。
 * Vite dev server 時は proxy 経由で API Gateway に接続。
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const value = useCognitoAuth();
  const [showSessionWarning, setShowSessionWarning] = useState(false);
  const [idleTimeoutMinutes, setIdleTimeoutMinutes] = useState(60);
  const settingsFetchedRef = useRef(false);

  /**
   * steering 551 / T6: fetch cache の identity namespace を同期する。
   *
   * **`user` オブジェクトの identity では作らない** — 上の `visibilitychange` ハンドラが
   * タブ復帰のたびに `setUser(refreshed)` を無条件で呼ぶため、フィールドが同一でも
   * object identity は変わる。**文字列フィールドから組む**こと。
   *
   * `user` が null の間 (認証 hydration 中 / logout 後) は scope を null にする。
   * `setIdentityScope` は値が変わったときに **全 entry を破棄**するので、
   * logout・org 切替・role 変更・別ユーザーの再ログインがこの 1 本で覆われる。
   */
  const identityScope = value.user
    ? `${value.user.userId}|${value.user.orgId}|${value.user.role}|${
        (typeof localStorage !== 'undefined' && localStorage.getItem('acecollect_client_id')) || ''
      }`
    : null;
  useEffect(() => {
    setIdentityScope(identityScope);
  }, [identityScope]);

  // 組織のセッション設定を取得
  useEffect(() => {
    if (!value.user || settingsFetchedRef.current) return;
    settingsFetchedRef.current = true;
    const fetchSettings = async () => {
      try {
        const { apiFetch } = await import('@/api/client');
        const data = await apiFetch<{ idleTimeoutMinutes: number }>('/admin/settings/session');
        if (data.idleTimeoutMinutes) {
          setIdleTimeoutMinutes(data.idleTimeoutMinutes);
        }
      } catch {
        // デフォルト値を維持
      }
    };
    fetchSettings();
  }, [value.user]);

  // ログアウト時にリセット
  useEffect(() => {
    if (!value.user) {
      settingsFetchedRef.current = false;
    }
  }, [value.user]);

  const handleWarning = useCallback(() => {
    setShowSessionWarning(true);
  }, []);

  // #406 Gate 8D-FR: dependency は **`value` オブジェクト全体ではなく `logout` だけ**にする。
  // value 全体に依存すると、value が変わるたびに handleTimeout の identity が変わり、
  // useIdleTimeout 側の effect が再登録され続ける (timer の張り直し) ため。
  const { logout } = value;
  const handleTimeout = useCallback(async () => {
    setShowSessionWarning(false);
    await logout();
    window.location.href = '/login?reason=idle';
  }, [logout]);

  const { resetTimer } = useIdleTimeout({
    timeoutMinutes: idleTimeoutMinutes,
    onWarning: handleWarning,
    onTimeout: handleTimeout,
    enabled: !!value.user,
  });

  const handleContinueSession = useCallback(async () => {
    setShowSessionWarning(false);
    resetTimer();
    // トークンを強制リフレッシュ
    try {
      const { fetchAuthSession } = await import('aws-amplify/auth');
      await fetchAuthSession({ forceRefresh: true });
    } catch {
      // リフレッシュ失敗は無視
    }
  }, [resetTimer]);

  return (
    <AuthContext.Provider value={value}>
      {children}
      {/* セッション警告モーダル */}
      {showSessionWarning && (
        <SessionWarningModal onContinue={handleContinueSession} onLogout={handleTimeout} />
      )}
    </AuthContext.Provider>
  );
}

/** セッション期限切れ警告モーダル */
function SessionWarningModal({
  onContinue,
  onLogout,
}: {
  onContinue: () => void;
  onLogout: () => void;
}) {
  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50">
      <div className="mx-4 w-full max-w-sm rounded-xl bg-white p-6 shadow-2xl dark:bg-gray-800">
        <div className="mb-4 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/30">
            <svg
              className="h-5 w-5 text-amber-600 dark:text-amber-400"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">セッション期限</h3>
        </div>
        <p className="mb-6 text-sm text-gray-600 dark:text-gray-400">
          無操作のため、まもなくセッションが切れます。継続する場合は「継続」ボタンを押してください。
        </p>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onLogout}
            className="flex-1 rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
          >
            ログアウト
          </button>
          <button
            type="button"
            onClick={onContinue}
            className="flex-1 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-indigo-500"
          >
            継続
          </button>
        </div>
      </div>
    </div>
  );
}

// ==============================
// useAuth フック
// ==============================

/** 認証コンテキストからユーザー情報・認証操作を取得する */
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth は AuthProvider 内で使用してください');
  }
  return ctx;
}
